import React, { useState, useEffect } from 'react';
import fr from 'facet-rest';
import { usePageContentContext } from 'Views/Page/Contexts/PageContentContext';
import WhatIfClientScenarioInsightContent from 'Views/Page/Content/Templates/FacetPlanClientScenarioInsightTemplate/InsightContent/Insight';
import {
  FDBox, FDStack, FDSecondaryActionButton, FDContentContainer, FDTextButton,
} from '@facet-wealth/design';
import { useSnackbarContext } from 'Context/SnackbarContext';
import WhatIfClientDisclaimerContainer from 'Components/ComponentLibrary/Organisms/WhatIfClientDisclaimer/WhatIfClientDisclaimerContainer';
import useEventTrackingLogEvent, { categories, eventTypes } from 'EventTracking/useEventTrackingLogEvent';
import useHomeContext from 'Context/HomeContext';
import SelectScenarioButtonPicker from 'Components/ComponentLibrary/Organisms/SelectScenarioButton';
import PageContentContainer from 'Components/ComponentLibrary/Molecules/Page/PageContentContainer';
import PageContentTop from 'Components/ComponentLibrary/Molecules/Page/PageContentTop';
import { WhatIfScenarioAssumptionsClientDisclaimer } from 'Components/ComponentLibrary/Organisms/WhatIfScenarioAssumptionsClientDisclaimer';
import { buildUseStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import useMobileLayout from 'Hooks/useMobileLayout';


const useStyles = buildUseStyles('ScenarioSelectionViewInsightsStepContent', ((theme) => ({
  insightContainer: {
    flex: '1 1 25%',
    minWidth: ({ isMobile }) => (isMobile ? undefined : theme.dimensions.md),
  },
})));

const Content = () => {
  const {
    pageContentResource: resource, setPageContentCollection, loadPageContentLink, isPageContentLoading,
  } = usePageContentContext();
  const { displayErrorMessage } = useSnackbarContext();
  const { logStandardizedEvent, logEventFailureWithError } = useEventTrackingLogEvent();
  const { refreshHomeResource } = useHomeContext();

  const isMobile = useMobileLayout();
  const classes = useStyles({ isMobile });

  const [loadingAction, setLoadingAction] = useState('');
  useEffect(() => {
    if (isPageContentLoading === false) {
      setLoadingAction('');
    }
  }, [isPageContentLoading]);

  const handleDetailsClick = async (scenarioInsightResource) => {
    logStandardizedEvent(categories.clientWhatIf, eventTypes.clickButton, 'Client Scenario Insight - Scenario selection button clicked', {
      eventProperties: {
        proposalScenarioId: scenarioInsightResource.attr('proposalScenarioId'),
      },
    });
    setLoadingAction(`viewDetails-${scenarioInsightResource.id()}`);
    try {
      await loadPageContentLink(scenarioInsightResource.relationship(fr.relationships.details.name).link('related'));
    } catch (err) {
      logEventFailureWithError('Client Scenario Insight - Failed to load scenario select page', err);
      displayErrorMessage(err, err.errors);
    }
  };

  const handleScenarioSelectionClick = async (scenarioInsightResource) => {
    logStandardizedEvent(categories.clientWhatIf, eventTypes.clickButton, 'Client Scenario Insight - Scenario selection button clicked', {
      eventProperties: {
        proposalScenarioId: scenarioInsightResource.attr('proposalScenarioId'),
      },
    });
    setLoadingAction(`selectScenario-${scenarioInsightResource.id()}`);
    try {
      await loadPageContentLink(scenarioInsightResource.relationship(fr.relationships.selectScenario.name).link('related'));
    } catch (err) {
      logEventFailureWithError('Client Scenario Insight - Failed to load scenario details page during scenario selection', err);
      displayErrorMessage(err, err.errors);
    }
  };

  const selectScenario = async (scenarioInsightResource) => {
    logStandardizedEvent(categories.clientWhatIf, eventTypes.clickButton, 'Client Scenario Insight - Scenario select button clicked', {
      eventProperties: {
        proposalScenarioId: scenarioInsightResource.attr('proposalScenarioId'),
      },
    });

    // Set the proposal scenario ID and submit the step
    resource.attr('proposalScenario', scenarioInsightResource.attr('proposalScenarioId'));
    try {
      const nextStepColl = await resource.submit();
      // Load the next step & refresh home resource since we now have a selected scenario
      refreshHomeResource();
      setPageContentCollection(nextStepColl);
    } catch (err) {
      logEventFailureWithError('Client Scenario Insight - Failed to select scenario', err);
      displayErrorMessage(err, err.errors);
    }
  };

  async function toggleInsights() {
    setLoadingAction('toggleInsights');
    await loadPageContentLink(resource.relationship(fr.relationships.toggleCommonInsights.name).link('related'));
  }

  return (
    <PageContentContainer maxWidth={'xl'}>
      {/* need to do this funky stuff because the sidenav overlaps the content otherwise. */}
      <FDBox paddingX={isMobile ? 0 : 8}>
        <PageContentTop
          title={resource.attr('title')}
          subtitle={resource.attr('subtitle')}
        />
      </FDBox>
      <FDStack direction={'column'} spacing={8} alignItems={'center'} justifyContent="flex-start">
        <FDBox paddingX={isMobile ? 0 : 8}>
          <FDStack spacing={2} wrap justifyContent={'center'} alignItems="flex-start">
            {resource?.attr('scenarioInsights')?.resources().list().map((scenarioInsightResource) => {
              return (
                <div
                  key={scenarioInsightResource.id()}
                  className={classes.insightContainer}
                >
                  <FDStack
                    direction={'column'}
                    spacing={2}
                    alignItems={'center'}
                    justifyContent="flex-start"
                  >
                    <WhatIfClientScenarioInsightContent
                      resource={scenarioInsightResource}
                      footerContent={resource.relationship(fr.relationships.toggleCommonInsights.name) && (
                      <FDTextButton
                        loading={loadingAction === 'toggleInsights'}
                        disabled={isPageContentLoading}
                        size={'sm'}
                        onClick={toggleInsights}
                        label={resource.relationship(fr.relationships.toggleCommonInsights.name).meta('prompt')}
                      />
                      )}
                    />
                    <FDStack
                      spacing={2}
                      alignItems="flex-start"
                      justifyContent={'center'}
                    >
                      <SelectScenarioButtonPicker
                        scenarioInsightResource={scenarioInsightResource}
                        buttonLabel={'Select Scenario'}
                        onConfirm={scenarioInsightResource.withRelationship(fr.relationships.selectScenario.name,
                          () => () => handleScenarioSelectionClick(scenarioInsightResource),
                          () => () => selectScenario(scenarioInsightResource))}
                        loading={loadingAction === `selectScenario-${scenarioInsightResource.id()}`}
                        disabled={isPageContentLoading}
                      />
                      <FDSecondaryActionButton
                        loading={loadingAction === `viewDetails-${scenarioInsightResource.id()}`}
                        disabled={isPageContentLoading}
                        label="View Details"
                        onClick={() => handleDetailsClick(scenarioInsightResource)}
                      />
                    </FDStack>
                  </FDStack>
                </div>
              );
            })}
          </FDStack>
        </FDBox>
        <FDBox margin={400}>
          <FDContentContainer maxWidth={'sm'}>
            <FDStack direction={'column'} spacing={2} alignItems={'center'} justifyContent="flex-start">
              <WhatIfScenarioAssumptionsClientDisclaimer resource={resource} />
              <WhatIfClientDisclaimerContainer />
            </FDStack>
          </FDContentContainer>
        </FDBox>
      </FDStack>
    </PageContentContainer>
  );
};

export default Content;
